import { MAX_VERSIONS_PER_ARTICLE } from "@productlane/lib/constants"

import type { HelpCenterArticleVersion, MutatorTx } from "../schema"

// Cap the number of stored versions per (article, language). When at the cap,
// drop the oldest non-head row that no OPEN/DRAFT proposal still references —
// those proposals need their base to render a live diff. Closed proposals may
// still reference it; the FK `ON DELETE SET NULL` clears their dead
// `baseVersionId` on delete. Returns the previous head version (the one whose
// `id` becomes `parentVersionId` on the new version row), so the caller
// doesn't have to refetch.
//
// Lives in its own file because the underlying decision — "which row, if any,
// is safe to drop?" — is a pure shape that we want to test against fixtures
// in `help-center-version-prune.test.ts` without spinning up Zero.
export async function pruneOldVersions(
	tx: MutatorTx,
	articleId: string,
): Promise<HelpCenterArticleVersion | undefined> {
	const existingVersions = await tx.query.helpCenterArticleVersions
		.where("articleId", "=", articleId)
		.where("language", "=", "en")
		.run()
	const sortedVersions = [...existingVersions].sort(
		(a, b) => a.createdAt - b.createdAt,
	)
	const previousHead = sortedVersions[sortedVersions.length - 1]

	if (existingVersions.length < MAX_VERSIONS_PER_ARTICLE) return previousHead

	for (const candidate of sortedVersions) {
		if (candidate.id === previousHead?.id) continue
		const referencing = await tx.query.helpCenterArticleProposals
			.where("baseVersionId", "=", candidate.id)
			.run()
		const stillReferenced = referencing.some(
			(p) => p.status === "OPEN" || p.status === "DRAFT",
		)
		if (!stillReferenced) {
			await tx.mutate.helpCenterArticleVersions.delete({ id: candidate.id })
			break
		}
	}
	return previousHead
}
